import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IonToggle, ToastController } from '@ionic/angular';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { finalize, of } from 'rxjs';
import { PanelHostService } from '../../../services/panel-host.service';
import { TripFinanceService } from '../../../services/trip-finance.service';
import { TripService } from '../../../services/trip.service';
import { ConfirmationService } from '../../../services/confirmation.service';
import { LegalDocumentsService } from '../../../services/legal-documents.service';
import { buildLegalDocumentSubmitReadiness } from '../../../services/forms/forms-legal';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { InfoPopoverComponent } from '../../ui/info-popover/info-popover.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';
import { TripAccessService } from '../../../services/trip-access/trip-access.service';
import { LegalSignatureSectionComponent } from '../../forms/legal-signature-section/legal-signature-section.component';
import { FaqComponent } from '../../faq/faq.component';
import { MONEY_IN_SETTINGS_FAQ_ITEM_IDS } from '../../faq/money-in-settings-faq-item-ids';
import { TripLinkQrComponent } from '../../trip-link-qr/trip-link-qr.component';

@Component({
  selector: 'app-money-in-settings-panel',
  templateUrl: './money-in-settings-panel.component.html',
  imports: [
    FormsModule,
    FaIconComponent,
    IonToggle,
    TranslocoDirective,
    ContentContainerComponent,
    InfoPopoverComponent,
    PanelBodyComponent,
    LegalSignatureSectionComponent,
    FaqComponent,
    TripLinkQrComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoneyInSettingsPanelComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly tripAccessService = inject(TripAccessService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly legalDocumentsService = inject(LegalDocumentsService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly activeLanguage = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  readonly trip = this.tripService.currentTrip;
  readonly access = computed(() => this.tripAccessService.context(this.trip.value()));
  readonly hasEnded = computed(() => this.trip.value()?.hasEnded ?? false);

  isEnabled = computed(() => (this.trip.value()?.partDonate ?? 0) === 1);
  isPublic = computed(() => (this.trip.value()?.partDonatePublic ?? 0) === 1);
  isParticipantPaymentsEnabled = computed(() => (this.access()?.partPay ?? 0) === 1);
  isStandardTrip = computed(() => this.access()?.isStandardTrip ?? false);
  isInterestPageActive = computed(() => (this.trip.value()?.interestPage ?? 0) === 1);
  hasAgreement = computed(() => Boolean(this.trip.value()?.fundraisingAgreementUrl));
  agreementUrl = computed(() => this.trip.value()?.fundraisingAgreementUrl ?? null);
  readonly tripId = computed(() => this.trip.value()?.id ?? null);

  /** Local intent to activate fundraising when no agreement PDF exists yet. */
  readonly activateIntent = signal(false);

  readonly activateChecked = computed(() =>
    this.hasAgreement() ? this.isEnabled() : this.activateIntent(),
  );

  private readonly agreementTemplateResource = rxResource({
    params: () => ({
      panelOpen: this.panelHost.activePanel() === 'money-in-settings',
      tripId: this.trip.value()?.id ?? null,
      needsTemplate:
        this.isStandardTrip() &&
        !this.hasAgreement() &&
        !this.hasEnded() &&
        this.activateIntent(),
      language: this.activeLanguage(),
    }),
    stream: ({ params }) => {
      if (!params.panelOpen || !params.needsTemplate || params.tripId == null) {
        return of(null);
      }
      return this.legalDocumentsService.getFundraisingAgreement(params.tripId);
    },
  });

  readonly agreementTemplate = computed(() => this.agreementTemplateResource.value() ?? null);
  readonly templateLoading = computed(() => this.agreementTemplateResource.isLoading());
  readonly templateError = computed(() => this.agreementTemplateResource.error() != null);

  private readonly sectionComponents = viewChildren(LegalSignatureSectionComponent);

  // Setup form
  setupMmGoal = signal<number>(0);
  setupSubmitting = signal(false);

  readonly setupSubmitReadiness = computed(() => {
    const template = this.agreementTemplate();
    if (!template) {
      return { canSubmit: false, firstIncompleteKey: null };
    }
    const sections = template.sections;
    const components = this.sectionComponents();
    const signedByKey: Record<string, boolean> = {};
    for (let i = 0; i < sections.length; i++) {
      const component = components[i];
      if (component) {
        signedByKey[sections[i].key] = component.signed();
      }
    }
    return buildLegalDocumentSubmitReadiness(sections, signedByKey, false, '');
  });

  readonly setupSubmitDisabled = computed(
    () => !this.setupSubmitReadiness().canSubmit || this.setupSubmitting(),
  );

  // MM goal edit
  mmGoalEdit = signal<number>(0);
  mmGoalSubmitting = signal(false);

  // Toggle fundraising
  fundraisingToggleSubmitting = signal(false);
  /** When false briefly, remounts ion-toggle so [checked] re-binds after a declined disable. */
  readonly fundraisingToggleMounted = signal(true);

  // Toggle public
  toggleSubmitting = signal(false);

  // Participant payments toggle
  participantPaymentsSubmitting = signal(false);

  readonly onboardingFaqItemIds = MONEY_IN_SETTINGS_FAQ_ITEM_IDS;
  readonly onboardingFaqPrefix = 'trip-finances.money-in-settings.faq';

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'money-in-settings') {
        this.clearActivateIntent();
        return;
      }
      this.mmGoalEdit.set(this.trip.value()?.partDonateMm ?? 0);
    });

    effect(() => {
      if (this.hasAgreement()) {
        this.clearActivateIntent();
      }
    });
  }

  private clearActivateIntent(): void {
    this.activateIntent.set(false);
    this.setupMmGoal.set(0);
  }

  private remountFundraisingToggle(): void {
    this.fundraisingToggleMounted.set(false);
    // ion-toggle updates its internal checked state on click before ionChange; remount
    // so [checked] re-binds. detectChanges between unmount and remount — queueMicrotask
    // batches both signal updates before CD and leaves the toggle visually off.
    this.changeDetectorRef.detectChanges();
    this.fundraisingToggleMounted.set(true);
  }

  onParticipantPaymentsChange(event: CustomEvent<{ checked: boolean }>): void {
    if (this.participantPaymentsSubmitting() || this.hasEnded()) return;
    const tripId = this.trip.value()?.id;
    if (!tripId) return;

    const checked = event.detail.checked;
    if (checked === this.isParticipantPaymentsEnabled()) return;

    this.participantPaymentsSubmitting.set(true);
    this.tripFinanceService
      .toggleParticipantPayments(tripId, { partPay: checked ? 1 : 0 })
      .pipe(finalize(() => this.participantPaymentsSubmitting.set(false)))
      .subscribe({
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.save-error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  async onActivateFundraisingChange(event: CustomEvent<{ checked: boolean }>): Promise<void> {
    if (this.fundraisingToggleSubmitting() || this.hasEnded()) return;

    const checked = event.detail.checked;
    if (checked === this.activateChecked()) return;

    if (!this.hasAgreement()) {
      if (checked) {
        this.activateIntent.set(true);
      } else {
        this.clearActivateIntent();
      }
      return;
    }

    const tripId = this.trip.value()?.id;
    if (!tripId) return;

    if (!checked) {
      const confirmed = await this.confirmationService.confirm({
        header: this.transloco.translate(
          marker('trip-finances.money-in-settings.disable-fundraising-confirm-header'),
        ),
        message: this.transloco.translate(
          marker('trip-finances.money-in-settings.disable-fundraising-confirm-message'),
        ),
      });
      if (!confirmed) {
        this.remountFundraisingToggle();
        return;
      }
    }

    this.fundraisingToggleSubmitting.set(true);
    this.tripFinanceService
      .toggleFundraising(tripId, { partDonate: checked ? 1 : 0 })
      .pipe(finalize(() => this.fundraisingToggleSubmitting.set(false)))
      .subscribe({
        error: async () => {
          this.remountFundraisingToggle();
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.save-error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  onPublicFundraisingChange(event: CustomEvent<{ checked: boolean }>): void {
    if (this.toggleSubmitting() || this.hasEnded()) return;
    const tripId = this.trip.value()?.id;
    if (!tripId) return;

    const checked = event.detail.checked;
    if (checked === this.isPublic()) return;

    this.toggleSubmitting.set(true);
    this.tripFinanceService
      .toggleFundraisingPublic(tripId, { fundraisingPublic: checked ? 1 : 0 })
      .pipe(finalize(() => this.toggleSubmitting.set(false)))
      .subscribe({
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.save-error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  submitSetup(): void {
    if (this.setupSubmitting() || !this.setupSubmitReadiness().canSubmit) {
      return;
    }

    const tripId = this.trip.value()?.id;
    const template = this.agreementTemplate();
    if (!tripId || !template) return;

    const mmGoal = this.setupMmGoal();
    const goal = typeof mmGoal === 'number' && !Number.isNaN(mmGoal) ? mmGoal : 0;

    const components = this.sectionComponents();
    const signatures: Record<string, { dataUrl: string | null; isEmpty: boolean }> = {};
    for (let i = 0; i < template.sections.length; i++) {
      const component = components[i];
      if (component) {
        signatures[template.sections[i].key] = component.getSignaturePayload();
      }
    }

    this.setupSubmitting.set(true);
    this.tripFinanceService
      .setupFundraising(tripId, {
        mmGoal: goal,
        signatures,
      })
      .pipe(finalize(() => this.setupSubmitting.set(false)))
      .subscribe({
        next: async () => {
          this.clearActivateIntent();
          this.mmGoalEdit.set(goal);
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.money-in-settings.setup-success')),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.save-error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  saveMmGoal(): void {
    if (this.mmGoalSubmitting()) return;
    const tripId = this.trip.value()?.id;
    if (!tripId) return;

    this.mmGoalSubmitting.set(true);
    this.tripFinanceService
      .updateMmGoal(tripId, { mmGoal: this.mmGoalEdit() })
      .pipe(finalize(() => this.mmGoalSubmitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.money-in-settings.mm-goal-saved')),
            duration: 3000,
            color: 'success',
          });
          await toast.present();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.save-error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }
}
